import React, { useState } from "react";
import { CardComp, SelectComp2, ButtonComp } from "../../../components";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { TableCompData } from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import TablePagination from "../../../components/Table/Pagination";
import { useGetStaffTransactionHistoryQuery } from "../../../store/admin/transactionSlice";
import { StaffTransactionHistoryHeader } from "../../../utils/TableHeader";
import StaffTransactionDetailsModal from "./modals/StaffTransactionDetailsModal";
import SearchBar from "../../Members/modules/SearchBar";
import { FiDownload } from "react-icons/fi";
import { storage } from "../../../utils/storage";
import { DropDownComp } from "../../../components";
import { BsChevronDown } from "react-icons/bs";

export default function StaffTransactionHistory() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [transactionType, setTransactionType] = useState("all");
  const [status, setStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: transactionData,
    isFetching,
    isLoading,
  } = useGetStaffTransactionHistoryQuery({
    page,
    limit: 20,
    type: transactionType === "all" ? undefined : transactionType,
    status: status === "all" ? undefined : status,
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
    search: search || undefined,
  });

  const transactions = transactionData?.transactions || [];
  const pagination = transactionData?.pagination || {};

  const transactionTypeOptions = [
    { label: "All Types", value: "all" },
    { label: "Contributions", value: "CONTRIBUTION" },
    { label: "Loans", value: "LOAN" },
    { label: "Withdrawals", value: "WITHDRAWAL" },
    { label: "Registration Fees", value: "REGISTRATION_FEE" },
    { label: "Fines", value: "ATTENDANCE_FINE" },
    { label: "SMS Topup", value: "SMS_TOPUP" },
  ];

  const statusOptions = [
    { label: "All Statuses", value: "all" },
    { label: "Successful", value: "SUCCESSFUL" },
    { label: "Pending", value: "PENDING" },
    { label: "Failed", value: "FAILED" },
    { label: "Declined", value: "DECLINED" },
  ];

  const TableDropDown = [
    {
      name: "View Details",
      action: (data) => {
        setSelectedTransactionId(data?.id);
        setIsModalOpen(true);
      },
    },
  ];

  const handleDownloadReport = (format = "excel") => {
    const params = new URLSearchParams({
      guard: "staff",
      format: format,
      type: transactionType === "all" ? "" : transactionType,
      status: status === "all" ? "" : status,
      from_date: fromDate || "",
      to_date: toDate || "",
      search: search || "",
    });

    const token = storage["cookieStorage"].get("access_token");
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const url = `${baseUrl}/cooperatives/transaction_histories/download?${params.toString()}`;

    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to download report");
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        
        if (format === "pdf") {
          // For PDF (which is actually HTML-to-Print), open in new tab
          const newWindow = window.open(blobUrl, "_blank");
          if (!newWindow) {
            alert("Please allow popups to view the printable report");
          }
        } else {
          // For Excel/CSV, trigger download
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = `Transaction_Report_${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        
        // Clean up the URL object after a delay to ensure the new window can load it
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      })
      .catch((err) => console.error("Download failed", err));
  };

  const downloadOptions = [
    {
      name: "Download as Excel (CSV)",
      action: () => handleDownloadReport("excel"),
    },
    {
      name: "Download as PDF (Printable)",
      action: () => handleDownloadReport("pdf"),
    },
  ];

  return (
    <div className="p-4">
      <div className="row align-items-center mb-4 g-3">
        <div className="col-md">
          <h4 className="fw-bold mb-0">Transaction History</h4>
          <p className="text-muted small mb-0">Monitor all financial activities in your branch</p>
        </div>
        <div className="col-md-auto" data-tour="staff-txn-export">
          <DropDownComp
            dropLabelClassName="bg-primary1 text-white px-4 py-2 rounded shadow-sm border-0 d-flex align-items-center gap-2"
            arrayComp={downloadOptions}
            input="name"
            DropDownText={
              <div className="d-flex align-items-center gap-2">
                <FiDownload /> 
                <span className="fw-bold">Download Report</span> 
                <BsChevronDown size={14} className="ms-1" />
              </div>
            }
            style={{ minWidth: "220px" }}
          />
        </div>
      </div>

      <CardComp cardClassName="border-0 shadow-sm mb-4" data-tour="staff-txn-filters">
        <div className="p-3">
          <div className="row g-3 align-items-end">
            <div className="col-lg-4 col-md-12">
              <label className="form-label small fw-bold mb-2">Search Transactions</label>
              <div className="transaction-history-search-wrapper">
                <SearchBar
                  placeholder="Search by reference, remark, member name..."
                  value={search}
                  onSearch={(val) => {
                    setSearch(val);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-lg-2 col-md-6">
              <SelectComp2
                LabelText="Transaction Type"
                selectedOption={transactionType}
                setSelectedOption={(opt) => {
                  setTransactionType(opt?.value || "all");
                  setPage(1);
                }}
                arrayComp={transactionTypeOptions}
                LabelClassName="fw-bold small mb-2"
                selectClassName="w-100 py-2 rounded border"
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <SelectComp2
                LabelText="Status"
                selectedOption={status}
                setSelectedOption={(opt) => {
                  setStatus(opt?.value || "all");
                  setPage(1);
                }}
                arrayComp={statusOptions}
                LabelClassName="fw-bold small mb-2"
                selectClassName="w-100 py-2 rounded border"
              />
            </div>
            <div className="col-lg-2 col-md-6">
              <div className="d-flex flex-column h-100">
                <label className="form-label small fw-bold mb-2">From Date</label>
                <input
                  type="date"
                  className="form-control py-2 rounded border shadow-none"
                  style={{ height: "42px", backgroundColor: "#fafafa" }}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-lg-2 col-md-6">
              <div className="d-flex flex-column h-100">
                <label className="form-label small fw-bold mb-2">To Date</label>
                <input
                  type="date"
                  className="form-control py-2 rounded border shadow-none"
                  style={{ height: "42px", backgroundColor: "#fafafa" }}
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </CardComp>

      <style>{`
        .transaction-history-search-wrapper .input-group.search {
          margin-bottom: 0 !important;
        }
        .transaction-history-search-wrapper .input-group-text {
          border-top-left-radius: 8px !important;
          border-bottom-left-radius: 8px !important;
          height: 42px;
        }
        .transaction-history-search-wrapper .form-control {
          border-top-right-radius: 8px !important;
          border-bottom-right-radius: 8px !important;
          height: 42px;
          border: 1px solid #ced4da !important;
        }
      `}</style>

      <CardComp cardClassName="border-0 shadow-sm" data-tour="staff-txn-table">
        <TableCompData
          marginBottom={"1rem"}
          columns={StaffTransactionHistoryHeader(TableDropDown)}
          data={transactions}
          progressPending={isLoading || isFetching}
          progressComponent={<TableSkeletonLoader rows={10} columns={8} />}
          message={
            !isLoading && !isFetching && transactions.length === 0 && (
              <EmptyTable Message="No transactions found matching your criteria." />
            )
          }
        />
        {!isLoading && !isFetching && transactions.length > 0 && (
          <div className="p-3">
            <TablePagination
              handlePageChange={setPage}
              {...pagination}
            />
          </div>
        )}
      </CardComp>

      {isModalOpen && selectedTransactionId && (
        <StaffTransactionDetailsModal
          transactionId={selectedTransactionId}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}

